// src/components/TaskTableRow.js
import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { toast } from "react-toastify";

import {
  getVideoLogEventSource,
  getAudioLogEventSource,
  getBounceAudioLogEventSource,
  PREDEFINED_MODES_MAP,
} from "../Api";

/* ─── helpers ─────────────────────────────────────────────── */
const fmtIdle = (sec) =>
  !Number.isFinite(sec) || sec < 0
    ? "0s"
    : sec < 60
    ? `${Math.floor(sec)}s`
    : sec < 3600
    ? `${Math.floor(sec / 60)}m ${Math.floor(sec % 60)}s`
    : `${Math.floor(sec / 3600)}h ${Math.floor((sec / 60) % 60)}m`;

/* localStorage snapshot helpers for Idle display - moved outside component */
const loadIdleSnapshot = (id) => {
  try {
    const raw = localStorage.getItem(`idleSnapshot:${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const saveIdleSnapshot = (id, obj) => {
  try {
    localStorage.setItem(`idleSnapshot:${id}`, JSON.stringify(obj));
  } catch {}
};

/* ----- Persisted Alert *Display* localStorage Helpers ----- */
const PERSISTED_ALERT_DISPLAY_KEY_PREFIX = "persistedAlertDisplayV2"; 

const loadPersistedAlertDisplay = (taskId) => {
  if (!taskId) return null;
  try {
    const raw = localStorage.getItem(`${PERSISTED_ALERT_DISPLAY_KEY_PREFIX}:${taskId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

const savePersistedAlertDisplay = (taskId, alertData) => {
  if (!taskId || !alertData) return;
  try {
    localStorage.setItem(`${PERSISTED_ALERT_DISPLAY_KEY_PREFIX}:${taskId}`, JSON.stringify(alertData));
  } catch {}
};

const clearPersistedAlertDisplay = (taskId) => {
  if (!taskId) return;
  try {
    localStorage.removeItem(`${PERSISTED_ALERT_DISPLAY_KEY_PREFIX}:${taskId}`);
  } catch {}
};

// SSE Event names for different audio task types
const AUDIO_SSE_EVENT_NAMES = {
    rtsp: "rtsp_audio_log",
    local_chunked: "local_audio_chunk_log",
    rtsp_bounce: "rtsp_bounce_audio_log",
    local_chunked_bounce: "local_audio_chunk_log",
    remote_chunked_bounce: "local_bounce_audio_chunk_log", 
};

/* ===================================================================== */

const TaskTableRow = memo(
  ({ item, isSelected, onSelectItem, onStopItem }) => {
    const [alertDisplayMessage, setAlertDisplayMessage] = useState({ text: "", time: null });
    const [internalLatestInfo, setInternalLatestInfo] = useState({
      text: item.message || item.status || "N/A",
      time: null,
    });

    const instSseRef = useRef(null);
    const idleTimerRef = useRef(null);
    const mounted = useRef(true);
    const prevItemIdRef = useRef(); 
    const sseConnectionTimeRef = useRef(null);

     const modeFlags = React.useMemo(() => {
     const isBounce =
       item.backendMode === PREDEFINED_MODES_MAP["Bounce Detector"] ||
       item.backendMode === "Bounce Audio Analysis" ||
       item.type === "rtsp_bounce" ||
     item.instanceData?.analysis_mode?.toLowerCase().includes("bounce") ||
      item.instanceData?.agent?.toLowerCase().includes("bounce");
        return {
            isIdle: item.backendMode === PREDEFINED_MODES_MAP["Idle Employee Tracker"],
            isFootfall: item.backendMode === PREDEFINED_MODES_MAP["Footfall Tracker"],
            isQueue: item.backendMode === PREDEFINED_MODES_MAP["Queue Monitor"],
            isTraffic: item.backendMode === PREDEFINED_MODES_MAP["Traffic Analyzer"],
            isAudioSentiment: item.backendMode === PREDEFINED_MODES_MAP["Customer Service Monitor"] ||
                             item.backendMode === "Audio Sentiment Analysis",
            isAudioBounce: isBounce,
            isAnyAudio: (item.backendMode === PREDEFINED_MODES_MAP["Customer Service Monitor"] ||
                         item.backendMode === "Audio Sentiment Analysis" ||
                         isBounce),
        };
    }, [item.backendMode, item.type]);

    const { isIdle, isFootfall, isQueue, isTraffic, isAudioSentiment, isAudioBounce, isAnyAudio } = modeFlags;

    const updateInternalStatus = useCallback((txt, when = null) => {
      if (!mounted.current) return;
      setInternalLatestInfo((prev) => {
        const newTime = when || new Date().toISOString();
        if (prev.text === txt && prev.time === newTime) return prev; 
        return { text: txt, time: newTime };
      });
    }, []); 

    const applyIdleStateForUI = useCallback(
      (d) => { 
        if (!mounted.current) return;
        const ts = d.utc_timestamp || d.timestamp_utc || d.timestamp || new Date().toISOString();
        if (idleTimerRef.current) { clearInterval(idleTimerRef.current); idleTimerRef.current = null; }

        if (d.event === "idle_start") {
          const startMs = new Date(ts).getTime() - (d.idle_duration_seconds ?? 0) * 1000;
          const updateIdleTimerDisplay = () => {
            if (!mounted.current) return;
            const currentIdleSeconds = (Date.now() - startMs) / 1000;
            updateInternalStatus(`Area Empty (Live Idle: ${fmtIdle(currentIdleSeconds)})`, ts);
          };
          updateIdleTimerDisplay(); 
          idleTimerRef.current = setInterval(updateIdleTimerDisplay, 1000);
        } else if (d.event === "idle_end") {
          updateInternalStatus(`Area Occupied (Idle Ended after: ${fmtIdle(d.idle_duration_seconds || 0)})`, ts);
        }
      },
      [updateInternalStatus] 
    );

    const cleanupSseAndTimers = useCallback(() => {
      if (instSseRef.current) { instSseRef.current.close(); instSseRef.current = null; }
      if (idleTimerRef.current) { clearInterval(idleTimerRef.current); idleTimerRef.current = null; }
    }, []);

    useEffect(() => {
      mounted.current = true;
      return () => {
          mounted.current = false;
          cleanupSseAndTimers();
          sseConnectionTimeRef.current = null; 
      };
    }, [cleanupSseAndTimers]);

    useEffect(() => {
      if (prevItemIdRef.current !== item.id) {
        const persistedAlert = loadPersistedAlertDisplay(item.id);
        if (mounted.current) setAlertDisplayMessage(persistedAlert || { text: "", time: null });
        sseConnectionTimeRef.current = null; 
        prevItemIdRef.current = item.id; 
      }
      
      cleanupSseAndTimers();
      sseConnectionTimeRef.current = null; 

      if (mounted.current) updateInternalStatus(item.message || item.status || "N/A", item.time || new Date().toISOString());

      if (!item.isRunning) {
        if (idleTimerRef.current) { clearInterval(idleTimerRef.current); idleTimerRef.current = null; }
        return; 
      }

      // Enhanced SSE connection logic for different task types
      let sseInstIdForLogStream, sseLogEventSource, sseLogEventName;
      
      if (isAnyAudio) {
        // For audio tasks, use different identifiers based on task type
        if (item.instanceData?.streamId) {
          // Use streamId from instanceData if available
          sseInstIdForLogStream = item.instanceData.streamId;
        } else if (item.stream_id) {
          // Fallback to stream_id from item directly
          sseInstIdForLogStream = item.stream_id;
        } else if (item.source) {
          // Fallback to source identifier
          sseInstIdForLogStream = item.source;
        } else {
          console.warn(`[TaskTR ${item.id}] No valid stream identifier found for audio task`);
          updateInternalStatus("N/A (No stream identifier)");
          return;
        }

        // Determine audio task type from multiple sources
        const audioTaskType = item.instanceData?.type || item.type || 'rtsp';
        
        if (audioTaskType.includes('bounce') || isAudioBounce) {
          sseLogEventSource = getBounceAudioLogEventSource(sseInstIdForLogStream);
          sseLogEventName = AUDIO_SSE_EVENT_NAMES[audioTaskType] || "rtsp_bounce_audio_log";
        } else {
          sseLogEventSource = getAudioLogEventSource(sseInstIdForLogStream);
          sseLogEventName = AUDIO_SSE_EVENT_NAMES[audioTaskType] || "rtsp_audio_log";
        }
      } else { 
        // Video tasks logic
        if (!item.instanceData?.instance_id) {
          console.warn(`[TaskTR ${item.id}] No instance_id found for video task`);
          updateInternalStatus("N/A (No instance ID)");
          return;
        }
        
        sseInstIdForLogStream = item.instanceData.instance_id;
        sseLogEventSource = getVideoLogEventSource(sseInstIdForLogStream);
        const backendModeStringForEvent = PREDEFINED_MODES_MAP[item.name] || item.backendMode;
        sseLogEventName = (backendModeStringForEvent || "unknown_video_mode").toLowerCase().replace(/ & /g, "_and_").replace(/ /g, "_") + "_log";
      }

      if (!sseLogEventSource || !sseLogEventName || !sseInstIdForLogStream) {
        console.warn(`[TaskTR ${item.id}] SSE configuration incomplete:`, {
          sseLogEventSource: !!sseLogEventSource,
          sseLogEventName,
          sseInstIdForLogStream
        });
        if (mounted.current) updateInternalStatus("N/A (SSE configuration error)");
        return;
      }
      
      console.log(`[TaskTR ${item.id}] Connecting to SSE:`, {
        streamId: sseInstIdForLogStream,
        eventName: sseLogEventName,
        isAudio: isAnyAudio,
        isBounce: isAudioBounce
      });
      
      instSseRef.current = sseLogEventSource;
      if (mounted.current) updateInternalStatus("Connecting to SSE…"); 

      sseLogEventSource.onopen = () => {
        if (!mounted.current) return;
        console.log(`[TaskTR ${item.id}] SSE connection opened for ${sseLogEventName}`);
        updateInternalStatus("SSE Connection Active");
        sseConnectionTimeRef.current = Date.now(); 
        if (isIdle) {
            const snap = loadIdleSnapshot(sseInstIdForLogStream);
            if (snap) { applyIdleStateForUI(snap); } 
            else { updateInternalStatus("Area Occupied (Idle: 0s)"); }
        }
      };

      sseLogEventSource.onerror = (event) => {
        if (!mounted.current) return;
        console.warn(`[TaskTR ${item.id}] Main SSE Error/Closed for ${sseLogEventName}. Event:`, event);
        if (instSseRef.current) { instSseRef.current.close(); instSseRef.current = null; } 
        updateInternalStatus("SSE connection issue");
        sseConnectionTimeRef.current = null; 
      };
      
      const handleInstanceLogEvent = (msg) => {
        if (!mounted.current) return;
        let d;
        try { d = JSON.parse(msg.data); } catch { console.warn("Bad JSON in instance SSE", msg.data); return; }
        
        console.log(`[TaskTR ${item.id}] Received SSE event:`, d);
        
        const eventTimestampForDisplay = d.utc_timestamp || d.timestamp_utc || d.timestamp || d.cross_utc || d.event_timestamp_utc || new Date().toISOString();
        
        const eventMs = new Date(eventTimestampForDisplay).getTime();
        const isTrulyLiveEvent =
          sseConnectionTimeRef.current !== null &&
          eventMs >= sseConnectionTimeRef.current;
          
        // ─────────────────────────── ALERT SENT ────────────────────────────────
        if (d.event_type === "alert_sent_backend" && d.details) {
          const alertId = d.sns_message_id || `${d.alert_trigger_type}-${d.utc_timestamp}`;
          const lastShown = loadPersistedAlertDisplay(item.id);

          if (!isTrulyLiveEvent) {
            if (!lastShown || lastShown.id !== alertId) {
              const silent = {
                id: alertId,
                text: `ALERT: ${d.details.message_for_ui || d.alert_trigger_type.replace(/_/g, " ")}`,
                time: d.utc_timestamp || new Date().toISOString(),
              };
              setAlertDisplayMessage(silent);
              savePersistedAlertDisplay(item.id, silent);
            }
            return;
          }

          if (lastShown && lastShown.id === alertId) return;

          const alertUIText = d.details.message_for_ui || `Alert: ${d.alert_trigger_type.replace(/_/g, " ")}`;
          const newAlert = {
            id: alertId,
            text: `ALERT: ${alertUIText}`,
            time: d.utc_timestamp || new Date().toISOString(),
          };

          setAlertDisplayMessage(newAlert);
          savePersistedAlertDisplay(item.id, newAlert);
          toast.info(`Backend alert for "${item.name}": ${alertUIText.substring(0, 70)}…`);
          return;
        }

        // ─────────────────── ALERT CONDITION CLEARED ───────────────────────────
        if (d.event_type === "alert_condition_cleared_backend" && d.details) {
          if (!isTrulyLiveEvent) return;

          clearPersistedAlertDisplay(item.id);
          setAlertDisplayMessage({ text: "", time: null });
          updateInternalStatus(
            d.details.message_for_ui || `${d.alert_trigger_type.replace(/_/g, " ")} condition cleared.`,
            d.utc_timestamp
          );
          toast.info(`Backend alert condition cleared for "${item.name}"`);
          return;
        }

        if (isIdle && (d.event === "idle_start" || d.event === "idle_end")) {
          applyIdleStateForUI(d); 
          if (sseConnectionTimeRef.current && new Date(eventTimestampForDisplay).getTime() >= sseConnectionTimeRef.current) {
            saveIdleSnapshot(sseInstIdForLogStream, { event: d.event, idle_duration_seconds: d.idle_duration_seconds ?? 0, utc_timestamp: eventTimestampForDisplay });
          }
          return;
        }
        
        if (isFootfall && d.event && (d.event === "entry" || d.event === "exit")) {
          const count = d.event === "entry" ? d.running_enter : d.running_exit;
          updateInternalStatus(`${d.event.toUpperCase()}: ${count}`, eventTimestampForDisplay);
          return;
        }
        
        if (isAnyAudio && d.customer_sentiment) { 
          const sentimentOrBounceStatus = d.customer_sentiment; 
          const concern = d.concern ? ` | Concern: ${(d.concern).substring(0, 50)}${d.concern.length > 50 ? '...' : ''}` : "";
          const solution = d.solution_provided ? ` | Action: ${(d.solution_provided).substring(0, 50)}${d.solution_provided.length > 50 ? '...' : ''}` : "";
          updateInternalStatus(`${isAudioBounce ? 'Bounce' : 'Sentiment'}: ${sentimentOrBounceStatus || "N/A"}${concern}${solution}`, eventTimestampForDisplay);
          return; 
        }

        if (isQueue && "queue_count" in d) {
          updateInternalStatus(`Queue: ${d.queue_count}, Wait: ${fmtIdle(d.avg_wait_seconds || 0)}`, eventTimestampForDisplay);
          return;
        }
        
        if (isTraffic && (d.event_type === "vehicle_crossed_line" || d.event_type === "vehicle_lp_detected" || d.event_type === "vehicle_lp_detected_delayed")) {
          const vCount = d.running_total ?? "N/A";
          const lp = d.lp_text ?? "N/A";
          updateInternalStatus(`Vehicles: ${vCount}, Last LP: ${lp}`, eventTimestampForDisplay);
          return;
        }
        
        if (d.message && d.event_type !== "alert_sent_backend" && d.event_type !== "alert_condition_cleared_backend") {
          updateInternalStatus(d.message, eventTimestampForDisplay);
        } else if (d.event && d.event_type !== "alert_sent_backend" && d.event_type !== "alert_condition_cleared_backend") {
          updateInternalStatus(d.event, eventTimestampForDisplay);
        }
      };

      sseLogEventSource.addEventListener(sseLogEventName, handleInstanceLogEvent);
      sseLogEventSource.addEventListener("status", (msg) => {
          if (!mounted.current) return;
          try {
              const statusData = JSON.parse(msg.data);
              if (statusData.message) updateInternalStatus(`SSE Status: ${statusData.message}`);
          } catch (e) { console.warn("Could not parse SSE status message", msg.data); }
      });
      sseLogEventSource.addEventListener("error", (msg) => {
          if (!mounted.current) return;
          try {
              const errorData = JSON.parse(msg.data);
               if (errorData.message) {
                  updateInternalStatus(`SSE Error: ${errorData.message}`);
                  toast.error(`SSE Error for ${item.name}: ${errorData.message}`);
              }
          } catch (e) { console.warn("Could not parse SSE error message", msg.data); }
      });

    }, [ 
      item.id, item.isRunning, item.instanceData, item.backendMode, item.name, item.stream_id, item.source, item.type,
      isAnyAudio, isAudioBounce, isIdle, isFootfall, isQueue, isTraffic, 
      applyIdleStateForUI, updateInternalStatus, cleanupSseAndTimers
    ]);

    const displayTimeStr = React.useMemo(() => {
      return alertDisplayMessage.time ? format(new Date(alertDisplayMessage.time), "HH:mm:ss") : "";
    }, [alertDisplayMessage.time]);

    const isDisabled = !item.isRunning && item.type !== "audio_static";

    const handleRowOrRadioClick = useCallback(() => {
      if (!isDisabled || item.type === "audio_static") { 
        onSelectItem(item.id);
      } else { 
        toast.warn(`Task "${item.name}" is not running right now.`);
      }
    }, [isDisabled, item.id, item.name, item.type, onSelectItem]);

    const handleStopClick = useCallback((e) => { e.stopPropagation(); onStopItem(item.id, item.type); }, [onStopItem, item.id, item.type]);

    const canStop = item.isRunning || item.type === "audio_static";

    const baseAgentName = Object.keys(PREDEFINED_MODES_MAP).find(
        key => PREDEFINED_MODES_MAP[key] === item.backendMode
    ) || item.backendMode;
    
    // For audio sentiment tasks, append analysis mode when available
    const agentDisplayFullName = isAudioSentiment && item.instanceData?.analysis_mode
        ? `${baseAgentName} (${item.instanceData.analysis_mode})`
        : baseAgentName; 

    return (
      <tr
        className={`task-table-row ${isSelected ? "selected" : ""} ${isDisabled ? "not-running-row" : ""}`}
        onClick={handleRowOrRadioClick}
      >
        <td className="radio-cell">
          <input
            type="radio"
            name={`task-selection-radio-group-${item.id}`}
            checked={isSelected}
            onChange={handleRowOrRadioClick}
            onClick={(e) => e.stopPropagation()}
            disabled={isDisabled && item.type !== "audio_static"}
            aria-label={`Select task ${item.name}`}
          />
        </td>
        <td>{item.name}</td>
        <td>{item.camera_display_name || item.camera_id || item.sourceInfo || "N/A"} <span className="item-id">(...{item.id.slice(-6)})</span></td>
        <td>{agentDisplayFullName}</td> 
        <td className={`status-cell status-${String(item.status || "").replace(/\s+/g, "-").toLowerCase()}`}>
          {item.status || "N/A"}
        </td>
        <td 
          className={`alert-info-cell ${alertDisplayMessage.text ? 'alert-active' : ''}`}
          title={alertDisplayMessage.text || internalLatestInfo.text} // Tooltip for full text
        >
          <div className="alert-info-content">
            {alertDisplayMessage.text ? (
              <>
                <span className="alert-icon" aria-hidden="true">⚠️ </span>
                {alertDisplayMessage.text}
                {displayTimeStr && <span className="alert-time"> ({displayTimeStr})</span>}
              </>
            ) : (
              internalLatestInfo.text
            )}
             {!alertDisplayMessage.text && internalLatestInfo.time && (
              <span className="internal-info-time"> ({format(new Date(internalLatestInfo.time), "HH:mm:ss")})</span>
             )}
          </div>
        </td>
        <td className="actions-cell">
          <button 
            className="action-button view-button" 
            onClick={(e) => { e.stopPropagation(); handleRowOrRadioClick(); }}
            disabled={isDisabled && item.type !== "audio_static"}
          >
            View
          </button>
          {canStop && (
            <button className="action-button stop-button" onClick={handleStopClick}>
              {item.type === "audio_static" ? "Clear" : "Stop"}
            </button>
          )}
        </td>
      </tr>
    );
  },
  (prevProps, nextProps) => { 
    const prevItem = prevProps.item;
    const nextItem = nextProps.item;
    return (
      prevProps.isSelected === nextProps.isSelected &&
      prevItem.id === nextItem.id &&
      prevItem.isRunning === nextProps.isRunning &&
      prevItem.status === nextProps.status &&
      (prevItem.message === nextItem.message || (!prevItem.message && !nextItem.message)) && 
      prevItem.name === nextItem.name &&
      prevItem.backendMode === nextProps.backendMode &&
      prevItem.type === nextItem.type &&
      ( (prevItem.instanceData === null && nextItem.instanceData === null) ||
        (prevItem.instanceData?.streamId === nextItem.instanceData?.streamId &&
         prevItem.instanceData?.instance_id === nextItem.instanceData?.instance_id &&
         prevItem.instanceData?.type === nextItem.instanceData?.type)
      )
    );
  }
);

TaskTableRow.displayName = 'TaskTableRow';

TaskTableRow.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    sourceInfo: PropTypes.string,
    camera_display_name: PropTypes.string,
    backendMode: PropTypes.string.isRequired,
    status: PropTypes.string,
    message: PropTypes.string,
    isRunning: PropTypes.bool,
    type: PropTypes.string,
    stream_id: PropTypes.string, // Added for audio tasks
    source: PropTypes.string, // Added for audio tasks
    instanceData: PropTypes.shape({
        streamId: PropTypes.string,
        instance_id: PropTypes.string,
        type: PropTypes.string, 
    }),
    time: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onSelectItem: PropTypes.func.isRequired,
  onStopItem: PropTypes.func.isRequired,
};

export default TaskTableRow;